/* ═══ CONSENT ═══
   The three-state consent record that replaces the five Mailchimp statuses
   (CRM v3 decision 7, 2026-09-06), and the ONE helper every consent write goes
   through. Pure — no Firestore, no DOM — so the app (contactCommit.js) and the
   plain node tools (tools/crm/*) share one set of rules instead of each
   inventing their own.

     opted_in      the person asked: the website mailing-list checkbox, a real
                   Mailchimp subscribe, or an offline consent an operator
                   recorded by hand WITH a note saying where it came from
     relationship  quoted or booked — the actual person on the thread, never a cc
     no_bulk       cc'd, vendors, unknown, or anyone who unsubscribed

   `setConsent()` is the only code path that may produce a new consent object.
   It throws rather than returning a wrong answer, because every caller here is
   a script or a save handler where a silent no-op is how a person who opted
   out ends up back on a list.

   The invariants:

   1. `opted_in` needs provenance. Only `website` or `mailchimp` can assert it
      on their own; `manual` may, but only with a note saying where the consent
      came from ("signed release 2026-09-02"). No other source ever can.
   2. `unsubscribed: true` is FINAL. The state is `no_bulk` and stays there.
      The single exception is a fresh opt-in the person performed themselves —
      source `website` or `mailchimp`, carrying a newer `at` — because those
      are the only two places they can ask again.
   3. Every consent carries `at` (a date, America/New_York) and `source`.

   Deciding WHETHER to write is the caller's job (see `consentLocked` and
   `CONSENT_RANK`); deciding WHAT a consent may say is this file's.          */

/** Bulk-eligible in ascending order of consent. `no_bulk` is the floor. */
export const CONSENT_RANK = { no_bulk: 0, relationship: 1, opted_in: 2 };

/** The two states bulk mail may go to. The audience resolver reads this. */
export const BULK_ELIGIBLE = ["opted_in", "relationship"];

/** Sources a consent write may declare. */
export const CONSENT_SOURCES = ["website", "mailchimp", "quote", "job", "manual", "migration"];

/** Only the person themselves can assert an opt-in unaided, and only in these
 *  two places. `manual` can too, but only carrying a note. */
export const OPT_IN_SOURCES = ["website", "mailchimp"];

export const isConsentState = (s) => Object.prototype.hasOwnProperty.call(CONSENT_RANK, String(s ?? ""));
export const isBulkEligible = (consent) =>
  !consent?.unsubscribed && BULK_ELIGIBLE.includes(String(consent?.state ?? ""));

/** A real calendar date in YYYY-MM-DD. The shape check alone accepts
 *  2026-02-31; the round-trip through Date is what rejects it, because JS
 *  normalizes an out-of-range day (2026-02-31 → March 3) and a date that
 *  survives unchanged is one that exists. Exported so callers can check a
 *  date BEFORE handing it to setConsent(), which throws on a bad one. */
export const isConsentDate = (v) => {
  const s = String(v ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
};
const isDate = isConsentDate;

/** A consent record in canonical shape. Unknown states floor to `no_bulk`
 *  rather than throwing: this is the READ path, and a malformed legacy value
 *  must not be readable as permission. */
export function makeConsent({ state = "no_bulk", source = "", at = "", unsubscribed = false, note = "" } = {}) {
  const s = isConsentState(state) ? state : "no_bulk";
  const out = {
    state: unsubscribed ? "no_bulk" : s,
    source: String(source || ""),
    at: String(at || ""),
    unsubscribed: !!unsubscribed,
  };
  if (String(note || "").trim()) out.note = String(note).trim();
  return out;
}

/** Read a record's consent, whatever shape it is in. A record written before
 *  this field existed reads as `no_bulk` — absence is not permission. */
export function readConsent(record) {
  return makeConsent(record?.consent || {});
}

/** Two consents that say the same thing. Used to skip pointless writes. */
export function sameConsent(a, b) {
  const x = makeConsent(a || {});
  const y = makeConsent(b || {});
  return x.state === y.state && x.source === y.source && x.at === y.at
    && x.unsubscribed === y.unsubscribed && (x.note || "") === (y.note || "");
}

/**
 * Is this record's consent locked against an automatic stamp?
 *
 * `opted_in` and `unsubscribed` are both answers the person gave. A quote
 * thread, a job, or a migration must leave them exactly as they are — the one
 * thing worse than missing consent is consent we overwrote.
 */
export function consentLocked(record) {
  const c = readConsent(record);
  if (c.unsubscribed || c.state === "opted_in") return true;

  /* A LEGACY unsubscribe nobody has migrated yet. Until migrate-consent.mjs
     puts a real consent object on the record, the only place that answer lives
     is `mailchimpStatus`, which readConsent() cannot see — so a quote link
     would read the record as "has said nothing" and promote it to
     `relationship`. That is the person's answer being overwritten by a save
     handler, which is exactly what this function exists to prevent.

     The cc exception: the 2026-08-26 ingest repair defaulted every person who
     arrived as a cc on somebody else's email to `mailchimpStatus:
     "unsubscribed"` without anyone unsubscribing from anything. That string on
     a cc-origin record is not an answer, so it does not lock. */
  return !Object.prototype.hasOwnProperty.call(record || {}, "consent")
    && String(record?.mailchimpStatus || "") === "unsubscribed"
    && String(record?.crmOrigin?.role || "") !== "cc";
}

/**
 * The new consent object for a write. THROWS when the write is not allowed.
 *
 * @param record  the contact/client whose consent is being changed (its
 *                current consent is what the invariants are checked against)
 * @param p.state         "opted_in" | "relationship" | "no_bulk"
 * @param p.source        one of CONSENT_SOURCES
 * @param p.at            YYYY-MM-DD, America/New_York
 * @param p.unsubscribed  true to record an unsubscribe (forces `no_bulk`)
 * @param p.note          required when asserting `opted_in` from `manual`
 * @returns {object} the consent object to write
 */
export function setConsent(record, { state, source, at, unsubscribed = false, note = "" } = {}) {
  const current = readConsent(record);
  const who = record?.id || record?._docId || "(record)";

  if (!isConsentState(state)) {
    throw new Error(`consent: "${state}" is not a consent state. Valid: ${Object.keys(CONSENT_RANK).join(", ")}`);
  }
  if (!CONSENT_SOURCES.includes(String(source || ""))) {
    throw new Error(`consent: "${source}" is not a consent source. Valid: ${CONSENT_SOURCES.join(", ")}`);
  }
  if (!isDate(at)) {
    throw new Error(`consent: at must be a real YYYY-MM-DD date (America/New_York), got ${JSON.stringify(at)}`);
  }

  const trimmedNote = String(note || "").trim();

  /* An unsubscribe always lands, from any source, and is the one transition
     that may move consent downward. */
  if (unsubscribed) return makeConsent({ state: "no_bulk", source, at, unsubscribed: true, note: trimmedNote });

  if (state === "opted_in") {
    const bySelf = OPT_IN_SOURCES.includes(source);
    const byHand = source === "manual" && !!trimmedNote;
    if (!bySelf && !byHand) {
      throw new Error(
        `consent: ${who} — opted_in needs provenance. Use source website or mailchimp, ` +
        "or source manual together with a note saying where the consent came from " +
        "(e.g. \"signed release 2026-09-02\")."
      );
    }
  }

  /* Unsubscribed is final. Only the person opting in again themselves, more
     recently than the unsubscribe, reverses it. A record with no date on its
     unsubscribe (migrated ones carry none) cannot outrank a dated opt-in. */
  if (current.unsubscribed) {
    const reOptIn = state === "opted_in" && OPT_IN_SOURCES.includes(source) && (!current.at || at > current.at);
    if (!reOptIn) {
      if (state === "no_bulk") {
        // Re-stating no_bulk on an unsubscribed record is harmless; the flag stays.
        return makeConsent({ state: "no_bulk", source, at, unsubscribed: true, note: trimmedNote });
      }
      throw new Error(
        `consent: ${who} unsubscribed on ${current.at || "an unknown date"} — refusing to set ${state}. ` +
        "Only a website or Mailchimp opt-in by the person themselves reverses an unsubscribe."
      );
    }
    return makeConsent({ state: "opted_in", source, at, unsubscribed: false, note: trimmedNote });
  }

  return makeConsent({ state, source, at, unsubscribed: false, note: trimmedNote });
}
